/**
 * auto-captions-timings — degenerate-timestamp sanitizer for Whisper output.
 *
 * Whisper's timestamped model keeps good word timings on speech, but on hard
 * audio (singing, long music, low SNR) it loses temporal alignment and emits
 * pathological timestamps that burn into unwatchable captions:
 *
 *   1. COLLAPSE — many consecutive words pinned to a single identical
 *      timestamp with ~zero duration. They burn as invisible / stacked
 *      captions (e.g. 30 cues all at 00:03:41,704 -> nothing shows for the
 *      whole tail of the song).
 *
 *   2. FREEZE — one word held across a huge silent gap because the next
 *      distinct timestamp is far away (e.g. "У меня много" frozen on screen
 *      for 59 seconds during an instrumental break).
 *
 * Neither is a transcription error we can fix (the words themselves may be
 * wrong — ASR can't read singing) — but the *timing* can always be made
 * watchable. `sanitize(words, totalDur)` is a pure post-process:
 *
 *   - spreads a collapsed run evenly across the window up to the next distinct
 *     timestamp (or the video end for a trailing dump), so every word shows
 *     and advances;
 *   - caps any single caption hold at MAX_HOLD seconds (kills the freeze);
 *   - guarantees a minimum visible duration and monotonic, in-bounds starts.
 *
 * A clean transcript with sane, increasing timestamps and no marathon holds
 * passes through UNCHANGED (changed === false), so this can never degrade
 * normal speech output. Mirrors the auto-captions-dedup.js guard pattern.
 *
 * Exposed as `window.__acTimings` (browser) and module.exports (node tests).
 */
(function () {
    'use strict';

    var DEFAULTS = {
        minDur: 0.30,   // every word shows at least this long (seconds)
        maxHold: 6.0,   // no caption frozen on screen longer than this
        eps: 0.05,      // word starts within this are "the same instant" (collapse)
    };

    function sanitize(words, totalDur, opts) {
        opts = opts || {};
        var MIN_DUR = opts.minDur != null ? opts.minDur : DEFAULTS.minDur;
        var MAX_HOLD = opts.maxHold != null ? opts.maxHold : DEFAULTS.maxHold;
        var EPS = opts.eps != null ? opts.eps : DEFAULTS.eps;

        if (!words || words.length < 2) return { words: words, changed: false, collapsedRuns: 0, capped: 0 };
        var n = words.length;
        var dur = (typeof totalDur === 'number' && isFinite(totalDur) && totalDur > 0)
            ? totalDur
            : ((words[n - 1].end || words[n - 1].start || 0) + 2);

        // Work on a shallow copy so callers can compare / discard if unchanged.
        var out = new Array(n);
        for (var c = 0; c < n; c++) out[c] = assign({}, words[c]);

        var changed = false, collapsedRuns = 0, capped = 0;

        // 1) Spread collapsed runs. A maximal run [a..b] whose starts are all
        //    within EPS of starts[a] is distributed evenly from base up to the
        //    next distinct start after the run (or the video end for a trailing
        //    dump). If that window is too small for the run, synthesize one from
        //    a nominal per-word duration so the words still become visible.
        var i = 0;
        while (i < n) {
            var a = i, base = num(out[a].start, 0), b = a;
            while (b + 1 < n && Math.abs(num(out[b + 1].start, base) - base) <= EPS) b++;
            if (b > a) {
                collapsedRuns++;
                var count = b - a + 1;
                var windowEnd = (b + 1 < n) ? num(out[b + 1].start, dur) : dur;
                if (!(windowEnd > base + count * MIN_DUR)) {
                    windowEnd = Math.min(dur, base + count * MIN_DUR);
                }
                var step = (windowEnd - base) / count;
                for (var k = a; k <= b; k++) {
                    out[k].start = base + (k - a) * step;
                    out[k].end = base + (k - a + 1) * step;
                }
                changed = true;
            }
            i = b + 1;
        }

        // 2) Clamp every word: in-bounds + monotonic starts, min duration, and
        //    no marathon hold. Clean words (sane gap, hold <= MAX_HOLD) keep
        //    their original end, so a clean transcript is left untouched.
        for (var p = 0; p < n; p++) {
            var st = num(out[p].start, p ? out[p - 1].start : 0);
            st = Math.max(0, Math.min(st, dur));
            if (p && st < out[p - 1].start) st = out[p - 1].start;
            out[p].start = st;

            var nextSt = (p + 1 < n) ? num(out[p + 1].start, Infinity) : Infinity;
            var en = num(out[p].end, st + MIN_DUR);
            if (en <= st) en = st + MIN_DUR;                                  // zero/neg -> visible
            if (en - st > MAX_HOLD) { en = st + MAX_HOLD; capped++; changed = true; }  // kill freeze
            if (en - st < MIN_DUR) en = st + MIN_DUR;                         // aim for min visibility
            if (nextSt > st && en > nextSt) en = nextSt;                      // ...but never overrun next
            en = Math.min(en, dur);
            if (en <= st) en = Math.min(dur, st + 0.02);                      // last-resort sliver
            out[p].end = en;
        }

        return { words: out, changed: changed, collapsedRuns: collapsedRuns, capped: capped };
    }

    function num(v, fallback) { return (typeof v === 'number' && isFinite(v)) ? v : fallback; }
    function assign(t, s) { for (var k in s) { if (Object.prototype.hasOwnProperty.call(s, k)) t[k] = s[k]; } return t; }

    /* Packed (speech pieces of the VAD laid end to end) → original time.
     * offsetMap — [{newStartSec, newEndSec, origStartSec}] from packPcmBySegments.
     * A WORD goes whole into the piece that holds its middle: with its two ends
     * mapped apart, a word written across a join started in one piece and ended
     * in the next, stretched over the pause the VAD dropped between them — «бл*»
     * 1.40 → 4.14 s and an em dash 10.79 → 20.07 s in her clip (chat 640,
     * 28.10.2026). A segment (a sentence) may span pieces: its ends map apart.
     * `asWords` — `items` is a word list; words inside items[k].words are always
     * words. `seen` — objects already mapped in this pass (the word list and the
     * segments share the same word objects). */
    function remapPacked(items, offsetMap, seen, asWords) {
        if (!Array.isArray(items) || !offsetMap || !offsetMap.length) return items;
        if (!seen && typeof WeakSet === 'function') seen = new WeakSet();
        var last = offsetMap[offsetMap.length - 1];
        function pieceAt(t) {
            for (var i = 0; i < offsetMap.length; i++) {
                var s = offsetMap[i];
                if (t >= s.newStartSec && t <= s.newEndSec + 0.01) return s;
            }
            return null;
        }
        function one(t) {
            var s = pieceAt(t);
            // after the last piece — its end
            return s ? s.origStartSec + (t - s.newStartSec) : last.origStartSec + (last.newEndSec - last.newStartSec);
        }
        function map(o, isWord) {
            if (!o || typeof o !== 'object') return;
            if (seen) { if (seen.has(o)) return; seen.add(o); }
            var s = isWord && isFinite(o.start) && isFinite(o.end) ? pieceAt((o.start + o.end) / 2) : null;
            if (!s) { o.start = one(o.start); o.end = one(o.end); return; }
            var a = Math.max(s.newStartSec, Math.min(s.newEndSec, o.start));
            var b = Math.max(a, Math.min(s.newEndSec, o.end));
            o.start = s.origStartSec + (a - s.newStartSec);
            o.end = s.origStartSec + (b - s.newStartSec);
        }
        for (var k = 0; k < items.length; k++) {
            var it = items[k];
            map(it, !!asWords);
            if (it && Array.isArray(it.words)) {
                for (var w = 0; w < it.words.length; w++) map(it.words[w], true);
            }
        }
        return items;
    }

    var api = { sanitize: sanitize, remapPacked: remapPacked, DEFAULTS: DEFAULTS };
    if (typeof window !== 'undefined') window.__acTimings = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
